import React, { useState } from 'react';
import styled from 'styled-components';
import btp2025 from './btp2025.json';
import btp2026 from './btp2026.json';

const ProjectTable = () => {
  const [selectedYear, setSelectedYear] = useState('2026');
  const [records, setRecords] = useState(btp2026);
  const [searchTerm, setSearchTerm] = useState('');

  const getCurrentData = () => {
    return selectedYear === '2026' ? btp2026 : btp2025;
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSearchTerm('');
    const newData = year === '2026' ? btp2026 : btp2025;
    setRecords(newData);
  };

  const handleFilter = (event) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchTerm(searchValue);
    
    const currentData = getCurrentData();
    const filteredData = currentData.filter((row) =>
      row.projectNo.toLowerCase().includes(searchValue) ||
      row.title.toLowerCase().includes(searchValue) ||
      row.supervisor.toLowerCase().includes(searchValue) ||
      (row.coSupervisor && row.coSupervisor.toLowerCase().includes(searchValue)) ||
      (row.students && row.students.some(student =>
        student.name.toLowerCase().includes(searchValue) ||
        student.rollNo.toLowerCase().includes(searchValue)
      ))
    );
    setRecords(filteredData);
  };

  const columns = [
    { name: 'Project No.', selector: row => row.projectNo },
    { name: 'Project Title', selector: row => row.title },
    { name: 'Supervisor', selector: row => row.supervisor },
    { name: 'Co-Supervisor', selector: row => row.coSupervisor || 'N/A' },
    {
      name: 'Students',
      selector: row => Array.isArray(row.students)
        ? row.students.map(student => student.name).join(", ")
        : "N/A"
    },
    {
      name: 'Roll No.',
      selector: row => Array.isArray(row.students)
        ? row.students.map(student => student.rollNo).join(", ")
        : "N/A"
    }
  ];

  return (
    <StyledWrapper>
      {/* Year Selector Boxes */}
      <div className="year-container">
        <div 
          className={`year-box year-2025 ${selectedYear === '2025' ? 'active' : ''}`}
          onClick={() => handleYearChange('2025')}
        >
          <span className="year-text">2024-25</span>
        </div>

        <div 
          className={`year-box year-2026 ${selectedYear === '2026' ? 'active' : ''}`}
          onClick={() => handleYearChange('2026')}
        >
          <span className="year-text">2025-26</span>
        </div>
      </div>

      <div className="table-container">
        {/* Search Input */}
        <input
          type="text"
          placeholder=" Search by any keyword..."
          value={searchTerm}
          onChange={handleFilter}
          className="search-input"
        />

        {/* Table */}
        <table>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col.name}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {records.length > 0 ? (
              records.map((record, idx) => (
                <tr key={idx}>
                  {columns.map((col, colIdx) => (
                    <td key={colIdx}>{col.selector(record)}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data">
                  No matching records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  /* Year Selector Boxes */
  .year-container {
    display: flex;
    gap: 20px;
    justify-content: center;
    align-items: center;
    padding: 30px 20px 20px 20px;
    flex-wrap: wrap;
  }

  .year-box {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 50px;
    border-radius: 15px;
    border: 2px solid;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 200px;
  }

  .year-box.year-2025 {
    background: linear-gradient(135deg, rgba(36, 16, 48, 0.8) 0%, rgba(30, 6, 38, 0.9) 100%);
    border-color: rgba(158, 28, 173, 0.5);
  }

  .year-box.year-2025:hover {
    background: linear-gradient(135deg, rgba(36, 16, 48, 1) 0%, rgba(30, 6, 38, 1) 100%);
    border-color: rgba(158, 28, 173, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(158, 28, 173, 0.3);
  }

  .year-box.year-2025.active {
    border-color: rgba(158, 28, 173, 1);
    box-shadow: 0 8px 25px rgba(158, 28, 173, 0.5);
    transform: translateY(-3px);
  }

  .year-box.year-2026 {
    background: linear-gradient(135deg, rgba(55, 42, 81, 0.8) 0%, rgba(3, 1, 30, 0.9) 100%);
    border-color: rgba(35, 72, 151, 0.6);
  }

  .year-box.year-2026:hover {
    background: linear-gradient(135deg, rgba(75, 62, 101, 0.6) 0%, rgba(8, 7, 77, 0.7)  100%);
    border-color: rgba(35, 72, 151, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(35, 72, 151, 0.3);
  }

  .year-box.year-2026.active {
    border-color: rgba(35, 72, 151, 1);
    box-shadow: 0 8px 25px rgba(35, 72, 151, 0.5);
    transform: translateY(-3px);
  }

  .year-text {
    font-size: 20px;
    font-weight: bold;
    font-family: Arial, Helvetica, sans-serif;
  }

  .year-box.year-2025 .year-text {
    color: #e8f1f8;
  }

  .year-box.year-2026 .year-text {
    color: #e8f1f8;
  }

  /* Table Container */
  .table-container {
    width: 90%;
    max-width: 1200px;
    margin: 30px auto;
    overflow-x: auto;
    font-family: Arial, Helvetica, sans-serif;
  }

  .search-input {
    width: 50%;
    padding: 10px;
    margin: 0 auto 20px auto;
    display: block;
    border-radius: 20px;
    border: solid 2px #4a148c;
    background-color: #222;
    color: #ddd;
    font-size: 16px;
    box-sizing: border-box;
    transition: all 0.3s ease-in-out;
    outline: none;
  }

  .search-input::placeholder {
    color: #888;
  }

  .search-input:hover,
  .search-input:focus {
    border-color: #9c27b0;
    box-shadow: 0px 0px 10px rgba(156, 39, 176, 0.6);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.5);
    border-radius: 6px;
    overflow: hidden;
  }

  th, td {
    padding: 12px;
    text-align: left;
  }

  th {
    background-color: #4a148c;
    color: #ffffff;
    font-weight: bold;
  }

  tbody tr:nth-child(odd) {
    background-color: #2c2c2c;
  }

  tbody tr:nth-child(even) {
    background-color: #1f1f1f;
  }

  tbody tr:hover {
    background-color: #3d235d;
  }

  td {
    color: #ddd;
  }

  .no-data {
    text-align: center;
    padding: 20px;
    color: #aaa;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .year-container {
      flex-direction: column;
      gap: 15px;
      padding: 20px;
    }

    .year-box {
      width: 100%;
      max-width: 300px;
    }

    .search-input {
      width: 80%;
    }
  }
`;

export default ProjectTable;
